// اجزای مشترک صفحات SCH (AP-SCH-01): مسیرها، کلیدهای Query، رنگ ثابت وضعیت (Design System)، قالب ساعت و تاریخ شمسی در منطقه
// زمانی شعبه (ADR-014) و قلاب‌های داده مشترک (گزینه‌های رزرو، دلایل لغو).
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AppointmentStatus,
  type BookingOptionsView,
  type BookingServiceOption,
  type CancelReasonView,
  type SchedulingMoney,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DEFAULT_TIME_ZONE, jalaliToBusinessDate, latinDigits, Money, StatusChip, toJalali, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement } from 'react';

export const TODAY_PATH = '/today';
export const CALENDAR_PATH = '/calendar';
export const SCHEDULES_PATH = '/settings/schedules';
export const APPOINTMENTS_PATH = '/appointments';
// AP-SCH-02 (IA §2.2، §2.3).
export const RESCHEDULE_QUEUE_PATH = '/reschedule-queue';
export const WAITLIST_PATH = '/waitlist';
export const MY_SCHEDULE_PATH = '/my-schedule';
export const POLICIES_PATH = '/settings/booking-policies';
export const APPOINTMENT_REPORT_PATH = '/reports/appointments';

export function appointmentPath(id: string): string {
  return `${APPOINTMENTS_PATH}/${id}`;
}

export const schKeys = {
  all: (tenantId: string | undefined) => ['scheduling', tenantId] as const,
  options: (tenantId: string | undefined, branchId: string | null, serviceId: string | null, q = '') =>
    ['scheduling', tenantId, 'options', branchId, serviceId, q] as const,
  slots: (tenantId: string | undefined, key: string) => ['scheduling', tenantId, 'slots', key] as const,
  preview: (tenantId: string | undefined, key: string) => ['scheduling', tenantId, 'preview', key] as const,
  daySheet: (tenantId: string | undefined, branchId: string | null, date: string | null) =>
    ['scheduling', tenantId, 'day-sheet', branchId, date] as const,
  calendar: (tenantId: string | undefined, branchId: string | null, date: string | null, by: string) =>
    ['scheduling', tenantId, 'calendar', branchId, date, by] as const,
  appointment: (tenantId: string | undefined, id: string) => ['scheduling', tenantId, 'appointment', id] as const,
  cancelReasons: (tenantId: string | undefined) => ['scheduling', tenantId, 'cancel-reasons'] as const,
  schedules: (tenantId: string | undefined, providerId: string | null, branchId: string | null) =>
    ['scheduling', tenantId, 'work-schedules', providerId, branchId] as const,
  exceptions: (tenantId: string | undefined, providerId: string | null) =>
    ['scheduling', tenantId, 'exceptions', providerId] as const,
  // AP-SCH-02.
  queue: (tenantId: string | undefined, includeClosed: boolean) => ['scheduling', tenantId, 'reschedule-queue', includeClosed] as const,
  suggestions: (tenantId: string | undefined, itemId: string) => ['scheduling', tenantId, 'suggestions', itemId] as const,
  waitlist: (tenantId: string | undefined, status: string) => ['scheduling', tenantId, 'waitlist', status] as const,
  policies: (tenantId: string | undefined) => ['scheduling', tenantId, 'policies'] as const,
  report: (tenantId: string | undefined, key: string) => ['scheduling', tenantId, 'report', key] as const,
  myQueue: (tenantId: string | undefined, date: string | null) => ['scheduling', tenantId, 'my-queue', date] as const,
  mySchedule: (tenantId: string | undefined, from: string | null) => ['scheduling', tenantId, 'my-schedule', from] as const,
  series: (tenantId: string | undefined, key: string) => ['scheduling', tenantId, 'series', key] as const,
};

const PERCENT = new Intl.NumberFormat('fa-IR', { style: 'percent', maximumFractionDigits: 1 });

/** درصد با ارقام و علامت فارسی (گزارش و سهم کانال). */
export function percent(value: number | string | null | undefined): string {
  return PERCENT.format(Number(value ?? 0));
}

/** رنگ ثابت وضعیت نوبت (design-system: رزرو، تأییدشده، حاضر، در حال خدمت، انجام‌شده، غیبت، لغو). */
export function statusTone(status: AppointmentStatus): StatusTone {
  switch (status) {
    case 'Booked':
      return 'booked';
    case 'Confirmed':
      return 'confirmed';
    case 'CheckedIn':
      return 'arrived';
    case 'InProgress':
      return 'in_service';
    case 'Completed':
      return 'done';
    case 'NoShow':
      return 'no_show';
    case 'Cancelled':
    case 'Rejected':
      return 'cancelled';
    default:
      return 'pending';
  }
}

/** نشان وضعیت پرداخت بیعانه با آیکن و متن (US-SCH-127؛ نه فقط رنگ). */
export function DepositPaymentChip({
  state,
  dueAt,
  timeZone = DEFAULT_TIME_ZONE,
}: {
  state: string | null | undefined;
  dueAt?: string | null;
  timeZone?: string;
}): ReactElement | null {
  const { t } = useTranslation('sch');
  if (!state) {
    return null;
  }
  const color =
    state === 'paid' ? 'success' : state === 'pending' ? 'warning' : state === 'unpaid' ? 'error' : state === 'at_center' ? 'default' : 'default';
  const icon =
    state === 'paid' ? (
      <CheckCircleOutlineIcon fontSize="inherit" />
    ) : state === 'pending' ? (
      <HourglassEmptyIcon fontSize="inherit" />
    ) : state === 'unpaid' ? (
      <ErrorOutlineIcon fontSize="inherit" />
    ) : state === 'at_center' ? (
      <StorefrontOutlinedIcon fontSize="inherit" />
    ) : (
      <AccountBalanceWalletOutlinedIcon fontSize="inherit" />
    );
  const label = t(`sch:day.deposit_state.${state}`, { defaultValue: t('sch:day.deposit') });
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      icon={icon}
      label={label}
      title={dueAt ? t('sch:day.deposit_due_tooltip', { time: formatTime(dueAt, timeZone) }) : t('sch:day.deposit_label')}
      data-testid="day-deposit"
      data-state={state}
    />
  );
  return dueAt ? <Tooltip title={t('sch:day.deposit_due_tooltip', { time: formatTime(dueAt, timeZone) })}>{chip}</Tooltip> : chip;
}

export function AppointmentStatusChip({ status, possibleNoShow = false }: { status: AppointmentStatus; possibleNoShow?: boolean }) {
  return (
    <StatusChip
      tone={possibleNoShow ? 'pending' : statusTone(status)}
      labelKey={possibleNoShow ? 'sch:status.possible_no_show' : `sch:status.${status}`}
    />
  );
}

/** ساعت دیواری شعبه (HH:mm با ارقام فارسی). */
export function formatTime(instant: string | null | undefined, timeZone: string = DEFAULT_TIME_ZONE): string {
  return instant ? toJalali(instant, { timeZone, pattern: 'HH:mm' }) : '';
}

/** نام روز و تاریخ شمسی (مثل «شنبه ۹ مهر ۱۴۰۵») برای تاریخ کسب‌وکاری yyyy-MM-dd. */
export function formatDay(businessDate: string | null | undefined, pattern = 'EEEE d MMMM yyyy'): string {
  return businessDate ? toJalali(businessDate, { pattern }) : '';
}

/** جمع روز به تاریخ کسب‌وکاری yyyy-MM-dd (بدون منطقه زمانی). */
export function addDays(businessDate: string, days: number): string {
  const [year, month, day] = businessDate.split('-').map(Number) as [number, number, number];
  const value = new Date(Date.UTC(year, month - 1, day + days));
  return value.toISOString().slice(0, 10);
}

/** تاریخ در URL شمسی yyyy-MM-dd (IA §2.3؛ تبدیل در لبه). */
export function toUrlDate(businessDate: string): string {
  return toJalali(businessDate, { pattern: 'yyyy-MM-dd', persianDigits: false });
}

/** تاریخ شمسی URL ← تاریخ کسب‌وکاری میلادی؛ نامعتبر ← null. */
export function fromUrlDate(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(latinDigits(value ?? '').trim());
  if (!match) {
    return null;
  }
  const business = jalaliToBusinessDate(Number(match[1]), Number(match[2]), Number(match[3]));
  return toUrlDate(business) === `${match[1]}-${match[2]!.padStart(2, '0')}-${match[3]!.padStart(2, '0')}` ? business : null;
}

/** روز هفته .NET (۰ = یکشنبه) برای تاریخ کسب‌وکاری. */
export function weekdayOf(businessDate: string): number {
  const [year, month, day] = businessDate.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** ترتیب روزهای هفته ایرانی (شنبه تا جمعه) با شماره .NET. */
/**
 * رنگ پیش‌فرض بلوک ارجاعی (BR-SCH-126): بلوک `Referral` بی رنگ سفارشی زرد دیده می‌شود تا در تقویم از زمان عادی
 * جدا باشد. رنگ سفارشی بلوک همیشه بر این پیش‌فرض مقدم است.
 */
export const REFERRAL_BLOCK_COLOR = '#f9a825';

/** رنگ بلوک: رنگ سفارشی، وگرنه پیش‌فرض نوع زمان (تهی برای زمان عادی = رنگ معمول تقویم). */
export function blockColor(timeType: string | null | undefined, color: string | null | undefined): string | null {
  if (color !== null && color !== undefined && color.length > 0) {
    return color;
  }

  return timeType === 'Referral' ? REFERRAL_BLOCK_COLOR : null;
}

export const IRANIAN_WEEK: readonly number[] = [6, 0, 1, 2, 3, 4, 5];

/** شعبه فعال نشست یا تنها شعبه مجاز. */
export function useActiveBranch(): { branchId: string | null; branchName: string | null } {
  const access = useAccess();
  const { activeTenant } = useTenantSession();
  const branchId = access.activeBranchId ?? (access.branchIds.length === 1 ? (access.branchIds[0] ?? null) : null);
  const branchName = activeTenant?.branches.find((branch) => branch.branchId === branchId)?.name ?? null;
  return { branchId, branchName };
}

/** گزینه‌های دیالوگ رزرو (خدمات، ارائه‌دهندگان واجد صلاحیت، اتاق‌ها، امروز و افق شعبه — C-AGENT-335). */
export function useBookingOptions(branchId: string | null, serviceId: string | null = null, enabled = true, q = '') {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: schKeys.options(session?.tenantId, branchId, serviceId, q),
    queryFn: (): Promise<BookingOptionsView> =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/booking-options', {
          params: { query: { branchId: branchId ?? undefined, serviceId: serviceId ?? undefined, q: q || undefined } },
        }),
      ),
    enabled: enabled && branchId !== null,
    staleTime: 60_000,
  });
}

/**
 * انتخاب خدمت با جستجوی سروری: booking-options فقط ۱۰۰ خدمت فعال نخست را برمی‌گرداند، پس در مرکزی با خدمات بیشتر
 * خدمت باید با `q` جستجو شود. خدمات دیده‌شده نگه داشته می‌شوند تا مقدار انتخاب‌شده پس از تغییر جستجو هم نمایش داده شود.
 */
export function useServiceSearch(branchId: string | null, enabled = true) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(input.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [input]);
  const result = useBookingOptions(branchId, null, enabled, query);
  const seen = useRef(new Map<string, BookingServiceOption>());
  const services = result.data?.services ?? [];
  for (const service of services) {
    seen.current.set(service.id, service);
  }
  return {
    services,
    search: setInput,
    find: (id: string | null | undefined) => (id ? seen.current.get(id) : undefined),
    isFetching: result.isFetching,
  };
}

/** دلایل لغو فعال (پیش‌فرض‌ها با کلید i18n). */
export function useCancelReasons(enabled = true) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: schKeys.cancelReasons(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/cancel-reasons')),
    enabled,
    staleTime: 300_000,
    select: (data): CancelReasonView[] => data.items.filter((item) => item.isActive),
  });
}

export function useReasonLabel() {
  const { t } = useTranslation('sch');
  return (reason: Pick<CancelReasonView, 'name' | 'nameKey'>) =>
    reason.nameKey ? t(reason.nameKey) : (reason.name ?? '');
}

/** مبلغ نوبت (ریال با واحد نمایش مستأجر) یا «قیمت تعریف نشده». */
export function PriceText({ price }: { price: SchedulingMoney | null | undefined }) {
  const { t } = useTranslation('sch');
  if (!price) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        {t('sch:booking.price_none')}
      </Typography>
    );
  }
  return <Money amountMinor={Number(price.amount)} currency={price.currency} />;
}

/** کاربر چندشعبه‌ای بدون شعبه فعال: راهنمای انتخاب شعبه از Header (AC-IAM-002.2). */
export function BranchRequired() {
  const { t } = useTranslation('sch');
  return (
    <Alert severity="info" data-testid="sch-branch-required">
      {t('sch:branch_required')}
    </Alert>
  );
}
